
import type { LucideIcon } from "lucide-react"
import { Moon, Sun, Settings, LogOut, ChevronDown, ChevronRight } from "lucide-react"
import { useTheme } from "next-themes"
import { useNavigate, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import * as React from "react"

import { cn } from "@/lib/utils"
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { useAuthContext } from "@/contexts/AuthContext"

interface NavProps {
  isCollapsed: boolean
  links: {
    title: string
    label?: string
    icon: LucideIcon
    variant: "default" | "ghost"
    href: string
    children?: {
      title: string
      label?: string
      href: string
    }[]
  }[]
  onNavigate?: (href: string) => void
  showSettingsShortcut?: boolean
}

export function Nav({ links, isCollapsed, onNavigate, showSettingsShortcut = true }: NavProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { setTheme, theme } = useTheme()
  const { t } = useTranslation()
  const { logout } = useAuthContext()
  const [expandedSections, setExpandedSections] = React.useState<Record<string, boolean>>({})

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  // Extract current language from pathname
  const currentLang = location.pathname.split('/')[1] || 'en'

  // Reemplazar "Analysis" con "SaaS" y añadir el badge "To confirm"
  const updatedLinks = links.map((link) => {
    if (link.title === "Analysis") {
      return { ...link, title: t('functions.saas'), label: t('functions.toConfirm') }
    }
    return link
  })

  const addLanguagePrefix = (href: string) => {
    if (href.startsWith('http://') || href.startsWith('https://')) return href
    // Keep /web outside i18n prefixes: it is opened in a new tab.
    if (href === '/web') return href
    const normalized = href.startsWith('/') ? href : `/${href}`
    return `/${currentLang}${normalized}`
  }

  const filteredLinks = updatedLinks.map((link) => ({
    ...link,
    href: addLanguagePrefix(link.href),
    children: link.children?.map((child) => ({
      ...child,
      href: addLanguagePrefix(child.href),
    })),
  }))

  // Compute active state from current pathname, ignoring the language prefix
  const normalize = (url: string) => {
    if (!url) return '/'
    let u = url
    if (!u.startsWith('/')) u = `/${u}`
    // remove trailing slash except root
    if (u.length > 1 && u.endsWith('/')) u = u.slice(0, -1)
    return u
  }
  const currentPathNoLang = normalize(location.pathname.replace(/^\/(en|es)/, ''))
  const isPathActive = (href: string) => {
    if (href.startsWith('http://') || href.startsWith('https://')) return false
    const linkPath = normalize(href.replace(/^\/(en|es)/, ''))
    return currentPathNoLang === linkPath || currentPathNoLang.startsWith(`${linkPath}/`)
  }

  const displayLinks = filteredLinks.map((link) => {
    const children = (link.children || []).map((child) => ({
      ...child,
      isActive: isPathActive(child.href),
    }))
    const hasActiveChild = children.some((child) => child.isActive)
    const isActive = isPathActive(link.href) || hasActiveChild
    return {
      ...link,
      variant: isActive ? "default" : "ghost" as const,
      isActive,
      hasChildren: children.length > 0,
      hasActiveChild,
      children,
    }
  })

  React.useEffect(() => {
    const nextState: Record<string, boolean> = {}
    for (const link of displayLinks) {
      if (link.hasChildren) {
        nextState[link.href] = link.href in expandedSections
          ? expandedSections[link.href]
          : link.hasActiveChild
      }
    }
    setExpandedSections((prev) => ({ ...prev, ...nextState }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  const toggleTheme = () => {
    setTheme(theme === "dark" ? "light" : "dark")
  }

  const handleNavigation = (href: string) => {
    // Check if it's an external URL
    if (href.startsWith('http://') || href.startsWith('https://')) {
      window.open(href, '_blank', 'noopener,noreferrer')
    } else {
      navigate(href)
    }
    // Always call onNavigate callback after navigation (e.g. to close mobile drawer)
    onNavigate?.(href)
  }

  const toggleSection = (href: string) => {
    setExpandedSections((prev) => ({ ...prev, [href]: !prev[href] }))
  }

  return (
    <TooltipProvider>
      <div className="flex h-[calc(100dvh-52px)] min-h-0 flex-col">
        <div className="flex min-h-0 flex-1 flex-col justify-start overflow-y-auto">
        <nav className={cn("grid gap-0.5 px-2 py-2", isCollapsed && "justify-items-center")}>
          {displayLinks.map((link) =>
            isCollapsed ? (
              <Tooltip key={link.href} delayDuration={0}>
                <TooltipTrigger asChild>
                  <Button
                    variant={link.variant as React.ComponentProps<typeof Button>["variant"]}
                    size="icon-nav"
                    className={cn(
                      link.variant === "default" &&
                        "bg-muted text-foreground hover:bg-muted dark:bg-muted dark:text-muted-foreground dark:hover:bg-muted dark:hover:text-white",
                    )}
                    onClick={() => handleNavigation(link.href)}
                  >
                    <link.icon className="h-5 w-5" />
                    <span className="sr-only">{link.title}</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right" className="flex items-center gap-4">
                  {link.title}
                  {link.label && <span className="ml-auto text-muted-foreground">{link.label}</span>}
                </TooltipContent>
              </Tooltip>
            ) : (
              <div key={link.href} className="space-y-0.5">
                <Button
                  variant={link.variant as React.ComponentProps<typeof Button>["variant"]}
                  size="nav"
                  className={cn(
                    link.variant === "default" &&
                      "bg-muted text-foreground hover:bg-muted dark:bg-muted dark:text-white dark:hover:bg-muted dark:hover:text-white",
                    "w-full justify-start",
                  )}
                  onClick={() => {
                    // In mobile drawer mode keep parent sections expandable without closing drawer.
                    if (link.hasChildren && onNavigate) {
                      toggleSection(link.href)
                      return
                    }
                    if (link.hasChildren) toggleSection(link.href)
                    handleNavigation(link.href)
                  }}
                >
                  <link.icon className="mr-2 h-5 w-5" />
                  {link.title}
                  {link.label && (
                    <span className={cn("ml-auto", link.variant === "default" && "text-foreground dark:text-white")}>
                      {link.label}
                    </span>
                  )}
                  {link.hasChildren && (
                    <span className={cn("text-muted-foreground", link.label ? "ml-2" : "ml-auto")}>
                      {expandedSections[link.href] ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </span>
                  )}
                </Button>

                {link.hasChildren && expandedSections[link.href] && (
                  <div className="ml-7 space-y-0.5 border-l border-border pl-2">
                    {link.children.map((child) => (
                      <Button
                        key={child.href}
                        variant={child.isActive ? "default" : "ghost"}
                        size="nav"
                        className={cn(
                          child.isActive &&
                            "bg-muted text-foreground hover:bg-muted dark:bg-muted dark:text-white dark:hover:bg-muted dark:hover:text-white",
                          "w-full justify-start"
                        )}
                        onClick={() => handleNavigation(child.href)}
                      >
                        {child.title}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            ),
          )}
        </nav>
        </div>

        <div className="mt-2 pb-[max(env(safe-area-inset-bottom),0px)]">
          <Separator />
          <div className={cn("w-full px-2 py-2", isCollapsed ? "flex flex-col items-center gap-1.5" : "flex items-center justify-between")}> 
            <Button
              variant="ghost"
              size="icon-nav"
              onClick={toggleTheme}
              title={t('functions.toggle_theme')}
            >
              <Sun className="h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
              <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
              <span className="sr-only">{t('functions.toggle_theme')}</span>
            </Button>
            {showSettingsShortcut && (
              <div className={cn("flex items-center gap-2", isCollapsed && "flex-col")}> 
                            <Button
                              variant="ghost"
                              size="icon-nav"
                              className="hover:bg-muted/50"
                              onClick={() => handleNavigation(`/${currentLang}/settings`)}
                              title={t('functions.settings')}
                            >
                              <Settings className="h-4 w-4" />
                              <span className="sr-only">{t('functions.settings')}</span>
                            </Button>
                            <Tooltip delayDuration={0}>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon-nav"
                                  className="hover:bg-destructive/10 hover:text-destructive"
                                  onClick={handleLogout}
                                  title={t('login.logout')}
                                >
                                  <LogOut className="h-4 w-4" />
                                  <span className="sr-only">{t('login.logout')}</span>
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="right">
                                {t('login.logout')}
                              </TooltipContent>
                            </Tooltip>
              </div>
            )}
          </div>
        </div>
      </div>
    </TooltipProvider>
  )
}
